import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

type WebShellContextValue = {
  header: React.ReactNode | null;
  setHeader: (ownerId: symbol, node: React.ReactNode) => void;
  clearHeader: (ownerId: symbol) => void;
};

type HeaderState = {
  ownerId: symbol;
  node: React.ReactNode;
} | null;

export const WebShellContext = createContext<WebShellContextValue | null>(null);

export function WebShellProvider({ children }: { children: React.ReactNode }) {
  const [headerState, setHeaderState] = useState<HeaderState>(null);

  const setHeader = useCallback((ownerId: symbol, node: React.ReactNode) => {
    setHeaderState({ ownerId, node });
  }, []);

  const clearHeader = useCallback((ownerId: symbol) => {
    setHeaderState(current => (current?.ownerId === ownerId ? null : current));
  }, []);

  const header = headerState?.node ?? null;
  const value = useMemo(
    () => ({ header, setHeader, clearHeader }),
    [clearHeader, header, setHeader],
  );

  return (
    <WebShellContext.Provider value={value}>
      {children}
    </WebShellContext.Provider>
  );
}

export function useWebShell() {
  const ctx = useContext(WebShellContext);
  if (!ctx) {
    throw new Error('useWebShell must be used within WebShellProvider');
  }
  return ctx;
}
